import React, { useState } from "react";
import ReactDOM from "react-dom";
import { FiX, FiInfo, FiCheckCircle } from "react-icons/fi";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const InfoIcon = FiInfo as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckCircleIcon = FiCheckCircle as React.FC<React.SVGProps<SVGSVGElement>>;

interface SlackChannel {
  id: string;
  name: string;
}

interface SlackChannelConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  joinedChannels: SlackChannel[];
}

/**
 * SlackChannelConfirmModal - Shows confirmation of joined channels and private channel instructions
 * Displays after auto-joining Slack public channels
 */
const SlackChannelConfirmModal: React.FC<SlackChannelConfirmModalProps> = ({
  isOpen,
  onClose,
  joinedChannels,
}) => {
  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal" style={{ maxWidth: "700px", maxHeight: "90vh" }}>
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title">
            <CheckCircleIcon style={{ width: "20px", height: "20px", color: "#10b981" }} />
            <span>SLACK CHANNELS CONNECTED</span>
          </div>
          <button type="button" className="su-modal-close" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="su-modal-body" style={{ padding: "24px", overflow: "auto", maxHeight: "calc(90vh - 180px)" }}>
          {/* Success message */}
          {joinedChannels.length > 0 && (
            <div
              style={{
                background: "#064e3b",
                border: "1px solid #10b981",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: 0, color: "#10b981", fontWeight: 600, fontSize: "14px" }}>
                ✅ Successfully joined {joinedChannels.length} public channel{joinedChannels.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {/* Joined channels list */}
          {joinedChannels.length > 0 && (
            <div style={{ marginBottom: "24px" }}>
              <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#d1d5db", marginBottom: "12px" }}>
                Channels the SendUpdates.ai app has joined:
              </h4>
              <div
                style={{
                  background: "#1e293b",
                  border: "1px solid #334155",
                  borderRadius: "6px",
                  padding: "12px",
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {joinedChannels.map((channel) => (
                  <div
                    key={channel.id}
                    style={{
                      padding: "6px 8px",
                      color: "#94a3b8",
                      fontSize: "13px",
                      fontFamily: "monospace",
                    }}
                  >
                    #{channel.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Private channels instructions */}
          <div
            style={{
              background: "#1e293b",
              border: "1px solid #3b82f6",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <InfoIcon style={{ width: "20px", height: "20px", color: "#3b82f6", marginTop: "2px", flexShrink: 0 }} />
              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#60a5fa", marginBottom: "8px", marginTop: 0 }}>
                  Adding to Private Channels
                </h4>
                <p style={{ margin: "0", fontSize: "13px", color: "#cbd5e1", lineHeight: "1.6" }}>
                  Our app can only join public channels automatically. To include private channels or conversation groups, simply <strong style={{ color: "#60a5fa" }}>@mention SendUpdates.ai</strong> in any channel or conversation you want to analyze.
                </p>
              </div>
            </div>
          </div>

          {/* Screenshot */}
          <div style={{ marginBottom: "20px" }}>
            <h4 style={{ fontSize: "14px", fontWeight: 600, color: "#d1d5db", marginBottom: "12px" }}>
              How to add the app to channels:
            </h4>
            <img
              src="/assets/tips/sendupdates-slack-snippet.png"
              alt="How to add SendUpdates.ai to Slack channels"
              style={{
                width: "100%",
                borderRadius: "6px",
                border: "1px solid #334155",
              }}
            />
          </div>

          {/* Acknowledgment checkbox */}
          <div
            style={{
              background: hasAcknowledged ? "#064e3b" : "#422006",
              border: hasAcknowledged ? "1px solid #10b981" : "1px solid #f59e0b",
              borderRadius: "8px",
              padding: "16px",
              transition: "all 0.2s ease",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                cursor: "pointer",
                fontSize: "13px",
                color: hasAcknowledged ? "#10b981" : "#fbbf24",
                transition: "color 0.2s ease",
              }}
            >
              <input
                type="checkbox"
                checked={hasAcknowledged}
                onChange={(e) => setHasAcknowledged(e.target.checked)}
                style={{
                  marginTop: "2px",
                  cursor: "pointer",
                  width: "16px",
                  height: "16px",
                  flexShrink: 0,
                  accentColor: "#10b981",
                }}
              />
              <span style={{ lineHeight: "1.5" }}>
                I have added the SendUpdates.ai slack app to the channels and conversations I want insights from.
              </span>
            </label>
            <div
              style={{
                marginTop: "12px",
                paddingTop: "12px",
                borderTop: hasAcknowledged ? "1px solid #047857" : "1px solid #78350f",
                fontSize: "12px",
                color: hasAcknowledged ? "#34d399" : "#fcd34d",
                lineHeight: "1.5",
                transition: "all 0.2s ease",
              }}
            >
              💡 <strong> If the SendUpdates.ai slack app is not added to a channel/conversation, data will not be pulled for generating insights.</strong>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="su-modal-footer" style={{ justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 24px",
              backgroundColor: "#667eea",
              color: "#FFFFFF",
              border: "1px solid #667eea",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#5568d3";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#667eea";
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SlackChannelConfirmModal;
